import { httpClient } from '@bs/core'
import type {
  CartDetail,
  CartRecoveryFlowTab,
  CartRecoveryOverview,
  CartRecoveryPerformance,
  CartRecoveryRangeParams,
  CartsParams,
  CartsResponse,
} from '../../types/cartRecovery'

const base = (storeId: string) =>
  `/stores/${storeId}/loyalty/flows/cart-recovery`

const rangeParams = ({ range, from, to }: CartRecoveryRangeParams) =>
  from ? { from, ...(to ? { to } : {}) } : range ? { range } : {}

export const getCartRecoveryOverview = async (
  storeId: string,
  params: CartRecoveryRangeParams,
): Promise<CartRecoveryOverview> =>
  (await httpClient.json.get(`${base(storeId)}/overview`, {
    params: rangeParams(params),
  })) as CartRecoveryOverview

export const getCartRecoveryCarts = async (
  storeId: string,
  params: CartsParams,
): Promise<CartsResponse> => {
  const { range, from, to, all, ...filters } = params
  const query = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== '' && v !== undefined),
  )
  return (await httpClient.json.get(`${base(storeId)}/carts`, {
    params: {
      ...rangeParams({ range, from, to }),
      ...query,
      ...(all ? { all: 1 } : {}),
    },
  })) as CartsResponse
}

export const getCartRecoveryCart = async (
  storeId: string,
  runId: string,
): Promise<CartDetail> => {
  const raw = (await httpClient.json.get(
    `${base(storeId)}/carts/${runId}`,
  )) as { cart: CartDetail }
  return raw.cart
}

export const stopCartRecoveryCart = async (
  storeId: string,
  runId: string,
): Promise<CartDetail> => {
  const raw = (await httpClient.json.post(
    `${base(storeId)}/carts/${runId}/stop`,
    {},
  )) as { cart: CartDetail }
  return raw.cart
}

export const sendNextCartRecoveryEmail = async (
  storeId: string,
  runId: string,
): Promise<{ cart: CartDetail; queued_email: number }> =>
  (await httpClient.json.post(
    `${base(storeId)}/carts/${runId}/send-next`,
    {},
  )) as { cart: CartDetail; queued_email: number }

export const getCartRecoveryPerformance = async (
  storeId: string,
  params: CartRecoveryRangeParams,
): Promise<CartRecoveryPerformance> =>
  (await httpClient.json.get(`${base(storeId)}/performance`, {
    params: rangeParams(params),
  })) as CartRecoveryPerformance

export const getCartRecoveryFlow = async (
  storeId: string,
  params: CartRecoveryRangeParams,
): Promise<CartRecoveryFlowTab> =>
  (await httpClient.json.get(`${base(storeId)}/flow`, {
    params: rangeParams(params),
  })) as CartRecoveryFlowTab
